import posthog from 'posthog-js';

import { logger } from '../lib/logger';
import type { AnalyticsEvent, EventProperties } from './events';

const log = logger.child({ module: 'analytics' });

/**
 * Which product an event came from.
 *
 * `credit_history` needed `Canvas`-prefixed task types for exactly this reason
 * and it applies again here: if canvas and flotoapps report into one PostHog
 * project, an event with no source is an event nobody can attribute. Set as a
 * property on every capture rather than trusted to the reader.
 */
const PLATFORM = 'canvas';

/**
 * PostHog, ported from `flotoapps/floto/src/lib/posthog/` with three
 * deliberate differences. Each is a decision, not a port that drifted:
 *
 * **1. Session recording is on in production only.** The reference records
 * everywhere. Here it is a product decision to watch real rooms, so it runs on
 * rooms.floto.ai and nowhere else: the test deployment, dev and every test
 * keep it off. A board shows the user's own product — screenshots of their
 * real pages — so this does send those pixels to PostHog. Inputs stay masked
 * (`maskAllInputs`), so typed text, passwords included, never leaves.
 *
 * **2. Autocapture is off for the same reason, one layer in.** Autocapture
 * records the text of what was clicked, and the things clicked on a board are
 * cards named after the user's screens — "Checkout payment step", "Admin
 * billing". That is their product's shape, arriving in an analytics tool as
 * element text nobody chose to send. Pageviews stay on; they carry a route,
 * not content. Everything else here is explicit and named.
 *
 * **3. No `/ingest` reverse proxy.** The reference proxies through a Next
 * rewrite to survive ad blockers. Firebase Hosting rewrites can only target
 * Cloud Run or Functions, so the equivalent would mean routing analytics
 * through `apps/api` — putting third-party traffic on the product's own
 * request path to dodge blockers. Not worth it. The cost is that a blocked
 * client reports nothing, which is the correct failure anyway.
 *
 * Gated on the key being present rather than on `import.meta.env.PROD`: both
 * the test and the production deploys run a production build, so `PROD` cannot
 * tell them apart. An unset key means analytics is silently off — the same
 * failure direction as `VITE_API_STREAM_ORIGIN`, and the reason `environment`
 * is registered as a super-property below.
 */
export function initAnalytics(): void {
  // Cast at the read, the convention `lib/mcp.ts` and `api/client.ts`
  // already follow — this app declares no ImportMetaEnv augmentation.
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) return;

  const environment =
    (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? 'development';

  try {
    posthog.init(key, {
      // No autocapture: see the note above. This is the single most important
      // line in this file.
      autocapture: false,
      api_host:
        (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
        'https://us.i.posthog.com',
      // On every route change, not just the first load. `true` records only
      // the page a tab opened on — in an SPA that is one pageview per visit,
      // so a funnel through `/auth/extension` → `/rooms/from-audit` →
      // `/sessions/…` could never see past the first step.
      capture_pageview: 'history_change',
      // Production only: see note 1 above.
      disable_session_recording: environment !== 'production',
      session_recording: { maskAllInputs: true },
      persistence: 'localStorage+cookie',
    });

    // A super-property, so it rides every event including the automatic
    // pageviews — which is what makes one PostHog project usable for a test
    // deployment and a production one at the same time.
    posthog.register({
      environment,
      platform: PLATFORM,
    });
  } catch (err) {
    // Analytics must never be able to stop the app booting.
    log.error({ err }, 'could not start analytics');
  }
}

/**
 * Tie events to a person.
 *
 * The uid, not the account id: a person is who PostHog counts, and the account
 * they are acting as rides along as a property so team activity is still
 * separable.
 */
export function identifyUser(
  uid: string,
  properties?: Record<string, unknown>,
): void {
  if (!posthog.__loaded) return;

  try {
    posthog.identify(uid, { platform: PLATFORM, ...properties });
  } catch (err) {
    log.error({ err }, 'could not identify the user');
  }
}

/** Sign-out. Without it the next person on this browser inherits the last
 *  one's identity, which is worse than no analytics. */
export function resetUser(): void {
  if (!posthog.__loaded) return;

  try {
    posthog.reset();
  } catch (err) {
    log.error({ err }, 'could not reset analytics');
  }
}

/**
 * Tag every event for the rest of this PostHog session — the browser
 * session, not the sign-in — with how it began.
 *
 * For arrivals whose path matters to every later event: someone the
 * extension sent to a room should be separable from someone who pasted a
 * link, in every funnel step after, without each call site having to know.
 * Session-scoped rather than `register`, so it does not stick to whatever
 * that person does next week.
 */
export function tagSession(properties: EventProperties): void {
  if (!posthog.__loaded) return;

  try {
    posthog.register_for_session(properties);
  } catch (err) {
    log.error({ err }, 'could not tag the session');
  }
}

/**
 * Record something a user did.
 *
 * A no-op when PostHog never initialised, which is every test and every dev
 * run without a key — `__loaded` rather than a flag of our own, so this stays
 * true whatever `init` decided.
 */
export function trackEvent(
  event: AnalyticsEvent,
  properties?: EventProperties,
): void {
  if (!posthog.__loaded) return;

  try {
    posthog.capture(event, properties);
  } catch (err) {
    log.error({ err, event }, 'could not record an event');
  }
}
